import { groupWith } from 'ramda';

import { formatTime } from '@utils/date.ts';
import { formatDirectionsDistance } from '@utils/distance.ts';
import { getPoiIconId } from '@utils/poi.ts';

import type { RouteStepRow, RouteStepsProps } from './RouteSteps.tsx';
import type { RouteLegKind, RouteNotice, RouteSegment, RouteSummaryProps, Translate } from './types.ts';

export type RouteStep = {
  icon?: string;
  /** Estimated minutes for this step. */
  eta: number;
  isAccessible?: boolean;
  primaryText?: string;
  secondaryTextUI?: string;
  /** Step distance in meters. */
  distance?: number;
  securityWaitTimes?: { queueTime?: number; isTemporarilyClosed?: boolean };
};

type RouteEndpoint = { title?: string };
type RouteSearchInput = {
  chosenPoi?: { category?: string; links?: readonly { url: string }[] };
};

/** Route preview data; endpoint and search-input indexes are aligned. */
export type RoutePreviewSource = {
  steps: readonly (readonly RouteStep[])[];
  totalTime?: number;
  /** Total route distance in meters, supplied by the caller rather than reconstructed from steps. */
  totalDistance: number;
  times?: readonly number[];
  /** Resolved endpoints supply both POI names and custom location titles. */
  endpoints?: readonly RouteEndpoint[];
  /** Only used for the final destination icon, not display names. */
  searchInputs?: readonly RouteSearchInput[];
  preferredUnits?: string;
};

export type RoutePreviewOptions = {
  T: Translate;
  locale?: string;
  now?: Date;
  showAccessibleNotice?: boolean;
};

const sum = (values: readonly number[]) => values.reduce((total, value) => total + value, 0);
/** Use the resolved endpoint title; raw search text does not identify a route stop. */
const getDestination = (route: RoutePreviewSource) => route.endpoints?.at(-1)?.title ?? '';
const getTotalMinutes = (route: RoutePreviewSource) =>
  route.totalTime ?? (route.times ? sum(route.times) : sum(route.steps.flat().map(step => step.eta)));
const getTotalDistance = (route: RoutePreviewSource, T: Translate) => {
  if (!Number.isFinite(route.totalDistance) || route.totalDistance < 0) {
    throw new Error('Route preview requires a finite, non-negative totalDistance in meters.');
  }
  return formatDirectionsDistance(route.totalDistance, route.preferredUnits ?? 'meters', T);
};

const getLegKind = (icon = ''): RouteLegKind => {
  if (icon === 'security' || icon.startsWith('wayfinding.security')) {
    return 'security';
  }
  if (icon.startsWith('wayfinding.train')) {
    return 'train';
  }
  if (icon.startsWith('wayfinding.bus')) {
    return 'bus';
  }
  return 'walk';
};

const getNoticeKey = (steps: readonly RouteStep[]) => {
  const icons = steps.map(step => step.icon ?? '');
  if (icons.some(icon => icon.startsWith('wayfinding.escalator'))) {
    return 'Route uses escalators';
  }
  if (icons.some(icon => icon.startsWith('wayfinding.stairs') || icon.startsWith('wayfinding.accessiblestairs'))) {
    return 'Route uses stairs';
  }
  if (icons.some(icon => icon.startsWith('wayfinding.elevator'))) {
    return 'Route uses elevators';
  }
  return undefined;
};

const formatMinutes = (minutes: number, T: Translate) =>
  T('getDirectionsFromTo:_minutes_ min', { minutes: Math.max(1, Math.round(minutes)) });

/** Adjacent walking instructions share a pill; each transport or security stage keeps its own. */
const buildLegSegments = (steps: readonly RouteStep[], T: Translate): RouteSegment[] => {
  const groups = groupWith(
    (previous, next) => getLegKind(previous.icon) === 'walk' && getLegKind(next.icon) === 'walk',
    steps,
  );
  return groups.map(group => ({
    kind: getLegKind(group[0].icon),
    duration: formatMinutes(sum(group.map(step => step.eta)), T),
  }));
};

const buildSegments = (legs: readonly (readonly RouteStep[])[], T: Translate): RouteSegment[] =>
  legs.flatMap<RouteSegment>((steps, index) => {
    const segments = buildLegSegments(steps, T);
    return index < legs.length - 1 ? [...segments, { kind: 'stop' }] : segments;
  });

/** Adapt calculated route totals, legs and display names without coupling the component to route state. */
export const buildRouteSummaryData = (
  route: RoutePreviewSource,
  { T, locale = 'en', now = new Date(), showAccessibleNotice = false }: RoutePreviewOptions,
): Omit<RouteSummaryProps, 'T' | 'onStart'> => {
  const totalMinutes = getTotalMinutes(route);
  const allSteps = route.steps.flat();
  const noticeKey = getNoticeKey(allSteps);
  let routeNotice: RouteNotice | undefined;
  if (noticeKey) {
    routeNotice = { kind: 'warning', message: T(`getDirectionsFromTo:${noticeKey}`) };
  } else if (showAccessibleNotice && allSteps.length > 0 && allSteps.every(step => step.isAccessible !== false)) {
    routeNotice = { kind: 'accessible', message: T('getDirectionsFromTo:This route is accessible') };
  }
  return {
    duration: formatMinutes(totalMinutes, T),
    eta: T('getDirectionsFromTo:_time_ eta', {
      time: formatTime(new Date(now.getTime() + totalMinutes * 60_000), locale),
    }),
    distance: getTotalDistance(route, T),
    segments: buildSegments(route.steps, T),
    routeNotice,
    destination: getDestination(route),
    destinationIcon: getDestinationIcon(route),
  };
};

/** The end icon also represents real walking; only discard empty arrival placeholders. */
const isEndpointMarker = (step: RouteStep) =>
  step.icon === 'wayfinding.start' ||
  (step.icon === 'wayfinding.end' && step.eta === 0 && (step.distance ?? 0) === 0 && !step.secondaryTextUI);

const formatStepMinutes = (minutes: number, T: Translate) =>
  minutes < 1
    ? T('getDirectionsFromTo:Less than a minute')
    : T('getDirectionsFromTo:_count_ minute', { count: Math.round(minutes) });

const buildStepRow = (
  step: RouteStep,
  route: RoutePreviewSource,
  T: Translate,
  endpointTitle?: string,
): RouteStepRow => {
  const kind = getLegKind(step.icon);
  let subtitle: string;
  if (kind === 'train' || kind === 'bus') {
    subtitle = T('getDirectionsFromTo:_count_ minute ride', { count: Math.round(step.eta) });
  } else if (kind === 'security') {
    const { queueTime, isTemporarilyClosed } = step.securityWaitTimes ?? {};
    subtitle = isTemporarilyClosed
      ? T('ui:Closed')
      : T('getDirectionsFromTo:_count_ minute wait time', { count: Math.round(queueTime ?? step.eta) });
  } else {
    subtitle = T('getDirectionsFromTo:_distance_ • _duration_', {
      distance: formatDirectionsDistance(step.distance ?? 0, route.preferredUnits ?? 'meters', T),
      duration: formatStepMinutes(step.eta, T),
    });
  }
  return {
    icon: step.icon === 'wayfinding.end' ? 'wayfinding.movingwalkway' : step.icon,
    title: [step.secondaryTextUI, endpointTitle || step.primaryText].filter(Boolean).join(' '),
    subtitle,
  };
};

/** Raw venue icon ID of the final endpoint's POI; the CDN, not the bundled registry, resolves it. */
const getDestinationIcon = (route: RoutePreviewSource) => {
  const poi = route.searchInputs?.[(route.endpoints?.length ?? 0) - 1]?.chosenPoi;
  return poi?.category ? getPoiIconId(poi) : undefined;
};

const buildStopRow = (route: RoutePreviewSource, stop: number, T: Translate): RouteStepRow => {
  const endpoint = route.endpoints?.[stop];
  if (!endpoint?.title || stop >= (route.endpoints?.length ?? 0) - 1) {
    return { stop, title: T('getDirectionsFromTo:Stop _stop_', { stop }) };
  }
  return {
    stop,
    title: T('getDirectionsFromTo:Arrive at _location_', { location: endpoint.title }),
    subtitle: T('getDirectionsFromTo:Stop _stop_', { stop }),
  };
};

/** Keep detailed instructions, adding intermediate stops and start/arrival rows for the preview. */
export const buildRouteStepsData = (
  route: RoutePreviewSource,
  { T }: Pick<RoutePreviewOptions, 'T'>,
): Omit<RouteStepsProps, 'T' | 'stickyHeader'> => {
  const destination = getDestination(route);
  const destinationIcon = getDestinationIcon(route);
  const steps = route.steps.flatMap((legSteps, legIndex) => {
    const contentSteps = legSteps.filter(step => !isEndpointMarker(step));
    const rows = contentSteps.map(step => {
      const endpointTitle =
        step === contentSteps.at(-1) && step.icon === 'wayfinding.end'
          ? route.endpoints?.[legIndex + 1]?.title
          : undefined;
      return buildStepRow(step, route, T, endpointTitle);
    });
    return legIndex < route.steps.length - 1 ? [...rows, buildStopRow(route, legIndex + 1, T)] : rows;
  });
  return {
    destination,
    distance: getTotalDistance(route, T),
    duration: formatMinutes(getTotalMinutes(route), T),
    steps: [
      {
        icon: 'wayfinding.start',
        title: T('getDirectionsFromTo:Start at _location_', { location: route.endpoints?.[0]?.title ?? '' }),
      },
      ...steps,
      {
        ...(destinationIcon ? { icon: destinationIcon, cdn: true } : { icon: 'wayfinding.start' }),
        title: T('getDirectionsFromTo:Arrive at _location_', { location: destination }),
      },
    ],
  };
};
